/**
 * "This came from somewhere else" (NIP-48).
 *
 * A bridge republishes a post from another network onto Nostr under a key the
 * bridge controls. Without saying so, a reader cannot tell an account that
 * publishes here from a mirror of an account that does not — and that difference
 * decides whether a reply will ever be seen by the person being mirrored. A reply
 * into a one-way bridge goes nowhere, silently.
 *
 * Two rules about how this is allowed to look, and both are about not
 * overclaiming:
 *
 *  - **It is never styled like a verification.** Sitting next to `ProvenanceChip`,
 *    which *is* a verification claim, that would be easy to get wrong. The bridge's
 *    signature proves the bridge published this and nothing whatsoever about the
 *    person being mirrored, so the chip is muted and carries no check mark.
 *  - **The source is shown, not linked.** The `id` is a stranger's string; making
 *    it an `href` would put an unvalidated URL from relay content into the page.
 *    Displaying it lets the reader see and copy where it came from, which is what
 *    they need, without Setu vouching for the destination by linking it.
 */

import { cn } from "@setu/ui";
import { Rss } from "lucide-react";
import { useState } from "react";

/** How much of the source id to show before it stops being informative. */
const MAX_ID = 96;

export function ProxyChip({
  id,
  sourceLabel,
  className,
}: {
  id: string;
  /** Already labelled for display, e.g. "ActivityPub". */
  sourceLabel: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const shown = id.length > MAX_ID ? `${id.slice(0, MAX_ID)}…` : id;

  return (
    <span className={cn("relative inline-flex", className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`Bridged from ${sourceLabel}. Show the original address.`}
        className={cn(
          "setu-mono inline-flex items-center gap-1 rounded px-1 py-0.5 text-2xs",
          "text-muted-foreground/70 transition-colors",
          "hover:bg-accent hover:text-foreground",
          "focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden",
        )}
      >
        <Rss aria-hidden className="size-3 shrink-0" />
        <span>{sourceLabel}</span>
      </button>
      {open ? (
        <span
          className={cn(
            "absolute right-0 bottom-full z-20 mb-1 w-max max-w-xs",
            "rounded-md border border-border bg-popover p-2 shadow-md",
          )}
        >
          <span className="block text-2xs text-muted-foreground">
            Bridged onto Nostr from {sourceLabel}. A reply here may not reach
            the original author.
          </span>
          {/* Text, not a link — see the module note. */}
          <span className="setu-mono mt-1 block break-all text-2xs">
            {shown}
          </span>
        </span>
      ) : null}
    </span>
  );
}
